export interface BasicCounts {
  ip: number;
  ex: number;
  den: number;
  met: number;
  not: number;
  exc: number;
}

/**
 * Return the counts CMS uses for a given aggregation algorithm.
 * @param strata Array of sanitized StrataData (numeric fields guaranteed)
 * @param algorithm Aggregation rule – "Weighted Average", "Simple Average", "1st Performance Rate", …
 * @param strataDefinitions Optional CMS-ordered definitions so we can pick the correct N-th stratum even if the array ordering differs.
 */
export function countsForAlgorithm(
  strata: Array<{ ip: number; ex: number; den: number; met: number; not: number; exc: number; name?: string; description?: string }>,
  algorithm: string,
  strataDefinitions: Array<{ name?: string; description: string }> = []
): BasicCounts {
  const blank = { ip: 0, ex: 0, den: 0, met: 0, not: 0, exc: 0 };

  // Weighted average uses totals – sum every stratum
  if (algorithm === 'Weighted Average') {
    return strata.reduce(
      (t, s) => ({
        ip: t.ip + s.ip,
        ex: t.ex + s.ex,
        den: t.den + s.den,
        met: t.met + s.met,
        not: t.not + s.not,
        exc: t.exc + s.exc,
      }),
      blank,
    );
  }

  // Algorithms like "1st Performance Rate", "2nd Performance Rate", …
  const nthMatch = /^(\d)(?:st|nd|rd|th) Performance Rate$/i.exec(algorithm);
  if (nthMatch) {
    const nth = parseInt(nthMatch[1], 10);

    // Prefer CMS-provided order if available
    if (strataDefinitions.length >= nth) {
      const def = strataDefinitions[nth - 1];
      const target = strata.find(
        (s) => s.name === def.name || s.description === def.description,
      );
      if (target) return target;
    }
    // Fallback to raw array position (nth-1) if definition lookup fails
    return strata[nth - 1] ?? blank;
  }

  // Simple Average (or anything unrecognised) – counts aren’t meaningful for the grid;
  // return zeroes so the UI can render “—”
  return blank;
}
